import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  Alert,
  ScrollView,
  Dimensions,
  Animated,
  LayoutAnimation,
  UIManager,
  Platform,
  Share,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  useNavigation,
  useRoute,
  useFocusEffect,
} from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import Button from '../../components/common/Button';
import Text from '../../components/common/Text';
import { useAuth } from '../../hooks/useAuth';
import {
  analyzeImage,
  saveSkinAnalysis,
  saveRecommendedColors,
  getAIColorRecommendations,
} from '../../api/colours';
import type {
  ColorPalette,
  UndertoneType,
} from '../../services/SkinUndertoneService';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import { CameraComponent } from '../../components/shared/CameraComponent';
import JewelryMetalSwatch from '../../components/shared/JewelryMetalSwatch';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSkinUndertone } from '../../hooks/useSkinUndertone';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSubscription } from '../../hooks/useSubscription';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { skinUndertoneService } from '../../services/SkinUndertoneService';
import { faceDetectionService } from '../../services/FaceDetectionService';
import { resolveFaceRegionForSkinAnalysis } from '../../utils/faceRoi';
import { legacyUndertoneForApi } from '../../utils/seasonUndertone';
import { trackButtonClick, trackAnalyticsEvent } from '../../utils/analytics';
import { shouldShowAnalysisFailure } from '../../utils/colorAnalysisFailure';
import type { PendingAnalysisResults } from '../../utils/colorAnalysisFailure';
import { usersAPI } from '../../api/users';
import { useQueryClient } from '@tanstack/react-query';
import { authKeys } from '../../hooks/useAuth';
import ColorAnalysisWizardModal from '../../components/shared/ColorAnalysisWizardModal';
import BlurredLockOverlay from '../../components/shared/BlurredLockOverlay';
import FloatingActionButton, {
  FloatingActionButtonOverlay,
} from '../../components/shared/FloatingActionButton';
import PremiumAIColorChatModal from '../../components/shared/PremiumAIColorChatModal';
import { PageHero } from '../../components';

const SHOW_COLOR_ANALYSIS_WIZARD_KEY = 'SHOW_COLOR_ANALYSIS_WIZARD';
const ENABLE_AI_FEATURES = false;

/** Colours per column in the full palette collection grid. */
const PALETTE_CHUNK = 10;

type ExtendedColorPalette = ColorPalette & {
  hairColors?: string[];
  eyeColors?: string[];
  jewelry?: Record<string, string>;
};

/** Merge AI clothing colours with static palette hair/eye/jewelry for save and display. */
function buildSavePalette(
  aiPalette: ColorPalette | null,
  staticPalette: ColorPalette | null
): ExtendedColorPalette | null {
  if (!staticPalette && !aiPalette) return null;
  if (aiPalette && staticPalette) {
    return {
      ...staticPalette,
      undertone: aiPalette.undertone,
      name: aiPalette.name,
      description: aiPalette.description,
      colors: aiPalette.colors,
      makeupRecommendations: staticPalette.makeupRecommendations,
    };
  }
  return (staticPalette ?? aiPalette) as ExtendedColorPalette;
}

function renderPaletteColumnSwatches(
  column: string[],
  colIdx: number,
  borderColor: string
) {
  return Array.from({ length: PALETTE_CHUNK }, (_, i) => {
    const color = column[i];
    if (!color) {
      return (
        <View
          key={`c-${colIdx}-${i}-spacer`}
          style={styles.paletteColumnSwatchSpacer}
        />
      );
    }
    return (
      <View
        key={`c-${colIdx}-${i}`}
        style={[
          styles.paletteColumnSwatch,
          { backgroundColor: color, borderColor },
        ]}
      />
    );
  });
}

// Enable LayoutAnimation for smooth transitions
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export interface ColourAnalysisScreenProps {
  onTabPress?: (tab: string) => void;
  /** When true, shows full feature with results blurred; used for public preview without sign-in */
  isPublicPreview?: boolean;
}

const ColourAnalysisScreen = ({
  onTabPress,
  isPublicPreview = false,
}: ColourAnalysisScreenProps) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  useAuth();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isPremium } = useSubscription();
  const { isMobile, containerPadding, screenWidth } = useResponsiveLayout();
  const queryClient = useQueryClient();

  const [, setScreenWidth] = useState(Dimensions.get('window').width);

  // Calculate available height excluding bottom navigation
  const screenHeight = Dimensions.get('window').height;
  const bottomNavHeight = isMobile ? 80 + insets.bottom + 10 : 0;
  const availableHeight = isMobile
    ? screenHeight - bottomNavHeight
    : screenHeight;

  /** Extra horizontal gutter on mobile (beyond `containerPadding` on the ScrollView). */
  const mobileScrollOuterExtra = 8;
  /** Scroll body inner horizontal padding on mobile (was 16). */
  const mobileScrollContentPaddingH = 22;

  /** Portrait width on mobile: centered with side margins (scroll + card padding). */
  const mobileProfileImageWidth = useMemo(() => {
    if (!isMobile) return null;
    const rowPad = Math.max(12, containerPadding);
    const scrollOuter = isPublicPreview
      ? Math.max(8, containerPadding - 8)
      : containerPadding + mobileScrollOuterExtra;
    const sideInset = scrollOuter + mobileScrollContentPaddingH + rowPad + 16;
    const usable = screenWidth - 2 * sideInset;
    return Math.min(280, Math.max(200, usable));
  }, [isMobile, containerPadding, screenWidth, isPublicPreview]);

  // Get route parameters
  const { fromWardrobe = false } =
    (route.params as {
      fromWardrobe?: boolean;
      showWardrobeButton?: boolean;
    }) || {};

  const [image, setImage] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCameraMode, setIsCameraMode] = useState(false);
  const [skinTone, setSkinTone] = useState<{
    category: string;
    confidence: number;
    color: string;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for premium prompt
  const [, setShowPremiumPrompt] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [hasCompletedAnalysis, setHasCompletedAnalysis] = useState(false);
  const [isSkinAnalysisSaved, setIsSkinAnalysisSaved] = useState(false);
  const [hasSavedCurrentAnalysis, setHasSavedCurrentAnalysis] = useState(false);
  const [, setSavedProfileId] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showWizardModal, setShowWizardModal] = useState(false);
  /** Which complete-palette column (chunk) is highlighted; UI-only, chunk size 10. */
  const [selectedPaletteColumnIndex, setSelectedPaletteColumnIndex] =
    useState(0);

  // AI palette overrides the static palette when Moonshot returns successfully
  const [aiPalette, setAiPalette] = useState<ColorPalette | null>(null);
  const [isAIPaletteLoading, setIsAIPaletteLoading] = useState(false);
  // Track the image URI we already fetched AI recommendations for (avoids duplicate calls)
  const aiPaletteImageRef = useRef<string | null>(null);
  /** Snapshot of analysis output before React state updates; avoids failure UI flash. */
  const pendingAnalysisResultsRef = useRef<PendingAnalysisResults | null>(null);

  const [aiInsightsModalVisible, setAiInsightsModalVisible] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        try {
          const value = await AsyncStorage.getItem(
            SHOW_COLOR_ANALYSIS_WIZARD_KEY
          );
          if (cancelled) return;
          if (value === '1') {
            await AsyncStorage.removeItem(SHOW_COLOR_ANALYSIS_WIZARD_KEY);
            setShowWizardModal(true);
          }
        } catch {
          // ignore
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [])
  );

  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.95));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for progress indicator
  const [, setProgressAnim] = useState(new Animated.Value(0));
  const [toastAnim] = useState(new Animated.Value(-100));
  const [errorToastAnim] = useState(new Animated.Value(-100));

  // Use the skin undertone hook
  const {
    undertone,
    colorPalette,
    seasonPrediction,
    analyzeUndertoneAdvanced,
    reset: resetUndertone,
    isLoading: undertoneLoading,
    error: undertoneError,
  } = useSkinUndertone();

  const showAnalysisFailure = shouldShowAnalysisFailure({
    image,
    isLoading,
    undertoneLoading,
    undertone,
    undertoneError,
    skinTone,
    pendingResults: pendingAnalysisResultsRef.current,
  });

  useEffect(() => {
    if (undertone && skinTone) {
      pendingAnalysisResultsRef.current = null;
    }
  }, [undertone, skinTone]);

  useEffect(() => {
    if (
      undertone &&
      skinTone &&
      skinTone.category !== 'Unknown' &&
      !undertoneError
    ) {
      setHasCompletedAnalysis(true);
    }
  }, [undertone, skinTone, undertoneError]);

  React.useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();

    // Listen for screen size changes
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });

    // Initialize services when the screen loads
    // This ensures they're ready for immediate use when users upload images
    const initializeServices = async () => {
      try {
        // Initialize skin undertone service
        if (!skinUndertoneService.isReady()) {
          await skinUndertoneService.initialize();
        }
        if (!faceDetectionService.isReady()) {
          await faceDetectionService.initialize();
        }

        // Initialize TensorFlow service
        const { tensorFlowService } = await import('../../utils/tensorflow');
        if (!tensorFlowService.isReady()) {
          await tensorFlowService.initialize();
        }
      } catch {
        // Init failed silently
      }
    };

    initializeServices();

    return () => subscription?.remove();
  }, [fadeAnim, slideAnim, scaleAnim]);

  // Toast animation effect
  React.useEffect(() => {
    if (isSkinAnalysisSaved) {
      // Slide down from top
      Animated.timing(toastAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();

      // Auto dismiss after 3 seconds
      const timer = setTimeout(() => {
        Animated.timing(toastAnim, {
          toValue: -100,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          setIsSkinAnalysisSaved(false);
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isSkinAnalysisSaved, toastAnim]);

  // Fetch AI colour recommendations once both undertone and skinTone are available
  // TODO: re-enable premium gate: if (!isPremium) return;
  useEffect(() => {
    if (!ENABLE_AI_FEATURES) {
      setAiPalette(null);
      setIsAIPaletteLoading(false);
      return;
    }
    if (!undertone || !skinTone || skinTone.category === 'Unknown' || !image) {
      return;
    }
    // Skip if we already fetched recommendations for this exact image
    if (aiPaletteImageRef.current === image) return;

    const fetchAIPalette = async () => {
      aiPaletteImageRef.current = image;
      setIsAIPaletteLoading(true);
      try {
        const skinToneData = skinUndertoneService.getSkinToneData();
        const result = await getAIColorRecommendations({
          undertone: legacyUndertoneForApi(undertone),
          skinToneCategory: skinTone.category,
          skinToneHex: skinTone.color ?? null,
          dominantColors:
            skinToneData?.dominantColors.map((c) => ({ hex: c.hex })) ?? [],
        });
        // Merge AI colours into the static palette shape so makeup
        // recommendations (which the AI does not return) are preserved.
        setAiPalette((prev) => ({
          undertone,
          name: result.paletteName,
          description: result.description,
          colors: result.recommendedColors,
          avoidColors: result.avoidColors,
          // Carry over makeup recommendations from the current static palette
          makeupRecommendations: (prev ?? colorPalette)?.makeupRecommendations,
        }));
      } catch {
        // Fall back silently to the static palette; do not surface error to user
        setAiPalette(null);
      } finally {
        setIsAIPaletteLoading(false);
      }
    };

    fetchAIPalette();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depends on undertone/skinTone/image; colorPalette captured at call time for makeup fallback
  }, [undertone, skinTone, image]);

  // Error toast animation effect
  React.useEffect(() => {
    if (error) {
      // Slide down from top
      Animated.timing(errorToastAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();

      // Auto dismiss after 4 seconds (longer for errors)
      const timer = setTimeout(() => {
        Animated.timing(errorToastAnim, {
          toValue: -100,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          setError(null);
        });
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [error, errorToastAnim]);

  const takePicture = useCallback(async () => {
    try {
      trackButtonClick('Take Photo', 'color-analysis-screen-upload-section');

      setIsCameraMode(true);
    } catch {
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  }, []);

  const finishImageAnalysis = useCallback((results: PendingAnalysisResults) => {
    pendingAnalysisResultsRef.current = results;
    setIsLoading(false);
  }, []);

  const analyzeImageUndertone = useCallback(
    async (imageUri: string): Promise<UndertoneType | null> => {
      try {
        // Determine if the current platform is web
        if (typeof document !== 'undefined') {
          // Create a canvas to get ImageData from the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new (
            window as typeof globalThis & { Image: new () => HTMLImageElement }
          ).Image() as HTMLImageElement;

          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = imageUri;
          });

          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);

          const imageData = ctx?.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );
          if (imageData) {
            const faceRegion =
              await resolveFaceRegionForSkinAnalysis(imageData);
            const undertoneResult = await analyzeUndertoneAdvanced(
              imageData,
              faceRegion
            );

            // Also analyze the image for skin tone data (but don't save automatically)
            await skinUndertoneService.analyzeImage(imageData);

            // Add a small delay to ensure hook state updates
            await new Promise((resolve) => setTimeout(resolve, 100));
            return undertoneResult;
          }
        } else {
          // Skip the undertone analysis on native platforms
        }
      } catch {
        setError('Failed to analyze skin undertone. Please try again.');
      }
      return null;
    },
    [analyzeUndertoneAdvanced]
  );

  const performEnhancedSkinToneAnalysis = useCallback(
    async (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D | null) => {
      if (!ctx) {
        return null;
      }
      const width = canvas.width;
      const height = canvas.height;
      const cx = width / 2;
      const cy = height / 2;

      // Define sampling area (center of image for uploaded photos)
      const samplingRadius = Math.min(width, height) * 0.3;
      const rx = samplingRadius;
      const ry = samplingRadius;

      // Multiple sampling points for better accuracy
      const samplingPoints = [
        { x: cx, y: cy, weight: 0.4 }, // Center
        { x: cx - rx * 0.3, y: cy - ry * 0.2, weight: 0.2 }, // Left area
        { x: cx + rx * 0.3, y: cy - ry * 0.2, weight: 0.2 }, // Right area
        { x: cx, y: cy - ry * 0.4, weight: 0.1 }, // Top area
        { x: cx, y: cy + ry * 0.3, weight: 0.1 }, // Bottom area
      ];

      const allPixels: Uint8ClampedArray[] = [];

      for (const point of samplingPoints) {
        // Check if the point is inside the sampling area
        const norm =
          Math.pow((point.x - cx) / rx, 2) + Math.pow((point.y - cy) / ry, 2);
        if (norm > 1) continue; // Skip points outside the area

        const sampleSize = 5; // 5x5 area for each point
        const pixels = new Uint8ClampedArray(sampleSize * sampleSize * 3);
        let pixelIndex = 0;

        for (
          let y = Math.floor(point.y - Math.floor(sampleSize / 2));
          y <= Math.floor(point.y + Math.floor(sampleSize / 2));
          y++
        ) {
          for (
            let x = Math.floor(point.x - Math.floor(sampleSize / 2));
            x <= Math.floor(point.x + Math.floor(sampleSize / 2));
            x++
          ) {
            // Check if this pixel is inside the sampling area
            const pixelNorm =
              Math.pow((x - cx) / rx, 2) + Math.pow((y - cy) / ry, 2);
            if (pixelNorm > 1) continue; // Skip pixels outside the area

            const i = (y * width + x) * 4;
            if (i >= 0 && i + 3 < canvas.width * canvas.height * 4) {
              const imageData = ctx.getImageData(x, y, 1, 1).data;
              pixels[pixelIndex] = imageData[0]; // R
              pixels[pixelIndex + 1] = imageData[1]; // G
              pixels[pixelIndex + 2] = imageData[2]; // B
              pixelIndex += 3;
            }
          }
        }

        const validPixels = pixels.slice(0, pixelIndex);
        if (validPixels.length > 0) {
          allPixels.push(validPixels);
        }
      }

      // Combine all pixel data
      const totalPixels = allPixels.reduce(
        (sum, pixels) => sum + pixels.length,
        0
      );
      const combinedPixels = new Uint8ClampedArray(totalPixels);
      let index = 0;

      for (const pixels of allPixels) {
        for (let i = 0; i < pixels.length; i++) {
          combinedPixels[index] = pixels[i];
          index++;
        }
      }

      // Import tensorFlowService dynamically to avoid circular dependencies
      const { tensorFlowService } = await import('../../utils/tensorflow');
      return await tensorFlowService.analyzeSkinTone(
        combinedPixels,
        width,
        height
      );
    },
    []
  );

  const analyzeImageSkinTone = useCallback(
    async (
      imageUri: string
    ): Promise<{
      category: string;
      confidence: number;
      color: string;
    } | null> => {
      try {
        if (typeof document !== 'undefined') {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new (
            window as typeof globalThis & { Image: new () => HTMLImageElement }
          ).Image() as HTMLImageElement;

          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = imageUri;
          });

          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);

          const imageData = ctx?.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );

          if (imageData && imageData.data && ctx) {
            const skinToneResult = await performEnhancedSkinToneAnalysis(
              canvas,
              ctx
            );
            if (skinToneResult) {
              setSkinTone(skinToneResult);
              return skinToneResult;
            }
          }
        }
      } catch {
        // Skin tone analysis is optional
      }
      return null;
    },
    [performEnhancedSkinToneAnalysis]
  );

  const analyzeColors = useCallback(async () => {
    if (!image) {
      setError('Please select an image to analyze');
      return;
    }

    // Use default name if coming from wardrobe, otherwise require name input
    const profileName = fromWardrobe
      ? `Colour Profile ${new Date().toLocaleDateString()}`
      : name;

    if (!fromWardrobe && !name.trim()) {
      setError('Please enter a name for this colour profile');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setAnalysisProgress(0);
      setHasCompletedAnalysis(false);
      setHasSavedCurrentAnalysis(false);

      const progressInterval = setInterval(() => {
        setAnalysisProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Call API to analyze image
      const response = await analyzeImage(image, profileName);

      clearInterval(progressInterval);
      setAnalysisProgress(100);
      setHasCompletedAnalysis(true);

      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

      // Store the profile ID for potential navigation later
      setSavedProfileId(response.id);

      // Don't navigate automatically - let user see the results first
      // Navigation will happen when user clicks "Save Analysis" or "Virtual Drape Test"
    } catch {
      setError('Error analyzing image. Please try again.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyzeImage from hook, navigation stable
  }, [image, name, fromWardrobe]);

  const resetImage = useCallback(() => {
    trackButtonClick('Start Over', 'color-analysis-screen-results-section');

    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setImage(null);
    setSkinTone(null);
    resetUndertone();
    setError(null);
    setHasCompletedAnalysis(false);
    setIsSkinAnalysisSaved(false);
    setHasSavedCurrentAnalysis(false);
    setAiPalette(null);
    setIsAIPaletteLoading(false);
    aiPaletteImageRef.current = null;
    pendingAnalysisResultsRef.current = null;
    setAiInsightsModalVisible(false);
    setSelectedPaletteColumnIndex(0);
  }, [resetUndertone]);

  const handleManualSave = useCallback(async () => {
    if (!skinTone || !undertone) {
      setError(
        'No analysis results to save. Please complete the analysis first.'
      );
      return;
    }

    trackAnalyticsEvent({
      event: 'button_click',
      button_name: 'Save Analysis',
      button_location: 'color-analysis-screen-results-section',
      skin_tone: skinTone.category,
      skin_tone_confidence: skinTone.confidence,
      undertone: undertone,
      has_color_palette: !!colorPalette,
    });

    try {
      setIsLoading(true);
      const skinToneData = skinUndertoneService.getSkinToneData();

      // Convert hex color to RGB format
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
              hex: hex,
            }
          : { r: 0, g: 0, b: 0, hex: hex };
      };

      if (skinToneData) {
        // Build dominant colors array - prioritize detected skin tone color
        let dominantColors = [...skinToneData.dominantColors];

        // Add detected skin tone color if available and not already in the array
        if (skinTone && skinTone.color) {
          const detectedSkinToneColor = hexToRgb(skinTone.color);
          // Check if this color is already in dominantColors
          const isAlreadyIncluded = dominantColors.some(
            (color) =>
              color.hex?.toUpperCase() ===
              detectedSkinToneColor.hex.toUpperCase()
          );
          if (!isAlreadyIncluded) {
            // Add detected skin tone color at the beginning
            dominantColors = [detectedSkinToneColor, ...dominantColors].slice(
              0,
              5
            );
          }
        }

        // Prefer merged palette (AI clothing + static hair/eye/jewelry)
        const savePalette = buildSavePalette(aiPalette, colorPalette);
        const saved = await saveSkinAnalysis(
          `Manual Save - ${new Date().toLocaleDateString()}`,
          legacyUndertoneForApi(undertone),
          skinTone?.category || skinToneData.skinToneCategory, // Use detected skin tone category
          skinTone?.confidence || skinToneData.confidence, // Use detected skin tone confidence (0.95)
          dominantColors, // Include detected skin tone color
          image // Include the image
        );
        setSavedProfileId(saved.id);

        // Save recommended colors and extended palette if available
        if (
          savePalette &&
          savePalette.colors &&
          savePalette.colors.length > 0
        ) {
          try {
            await saveRecommendedColors(saved.id, savePalette.colors, {
              hairColors: savePalette.hairColors,
              eyeColors: savePalette.eyeColors,
              jewelry: savePalette.jewelry,
            });
          } catch {
            // Save colors failed
          }
        }

        setIsSkinAnalysisSaved(true);
        setHasSavedCurrentAnalysis(true);

        try {
          const updatedUser = await usersAPI.trackBadgeUsage('color_analysis');
          if (updatedUser) {
            queryClient.invalidateQueries({ queryKey: authKeys.user() });
          }
        } catch {
          // Badge tracking failed
        }

        // Reset the saved status after 3 seconds
        setTimeout(() => setIsSkinAnalysisSaved(false), 3000);
      } else {
        // Fallback to basic data if service data is not available
        // Convert hex color to RGB format
        const hexToRgb = (hex: string) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
                hex: hex,
              }
            : { r: 0, g: 0, b: 0, hex: hex };
        };

        // Use detected skin tone color as dominant color
        const detectedSkinToneColor = skinTone
          ? hexToRgb(skinTone.color)
          : null;
        const dominantColors = detectedSkinToneColor
          ? [detectedSkinToneColor]
          : [];

        const savePaletteAlt = buildSavePalette(aiPalette, colorPalette);
        const saved = await saveSkinAnalysis(
          `Manual Save - ${new Date().toLocaleDateString()}`,
          legacyUndertoneForApi(undertone),
          skinTone?.category || 'Unknown', // Use detected skin tone category
          skinTone?.confidence || 0, // Use detected skin tone confidence (0.95)
          dominantColors, // Use detected skin tone color
          image
        );
        setSavedProfileId(saved.id);

        // Save recommended colors and extended palette if available
        if (
          savePaletteAlt &&
          savePaletteAlt.colors &&
          savePaletteAlt.colors.length > 0
        ) {
          try {
            await saveRecommendedColors(saved.id, savePaletteAlt.colors, {
              hairColors: savePaletteAlt.hairColors,
              eyeColors: savePaletteAlt.eyeColors,
              jewelry: savePaletteAlt.jewelry,
            });
          } catch {
            // Save colors failed
          }
        }

        setIsSkinAnalysisSaved(true);
        setHasSavedCurrentAnalysis(true);

        try {
          const updatedUser = await usersAPI.trackBadgeUsage('color_analysis');
          if (updatedUser) {
            queryClient.invalidateQueries({ queryKey: authKeys.user() });
          }
        } catch {
          // Badge tracking failed
        }

        setTimeout(() => setIsSkinAnalysisSaved(false), 3000);
      }
    } catch {
      setError('Failed to save analysis. Please try again.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- aiPalette, colorPalette, image, queryClient captured via closure; full deps would cause loops
  }, [skinTone, undertone]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for share feature
  const handleShareAnalysis = useCallback(async () => {
    if (!image || !(aiPalette ?? colorPalette)) return;

    try {
      const shareLabel =
        seasonPrediction?.subtypeLabel ||
        aiPalette?.name ||
        colorPalette?.name ||
        'seasonal palette';
      const message = `I just discovered my perfect colour palette with ColorSense! 🎨 My season is ${shareLabel} and I'm loving these colors. Check it out!`;
      await Share.share({
        message,
        url: 'https://mycoloursense.com',
      });
    } catch {
      // Share failed
    }
  }, [image, seasonPrediction, aiPalette, colorPalette]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for premium CTA
  const handlePremiumUpgrade = useCallback(() => {
    navigation.navigate('PremiumPaywall');
  }, [navigation]);

  // Get undertone color for chip
  const getUndertoneColor = useCallback(
    (undertoneType: string) => {
      switch (undertoneType) {
        case 'spring':
          return colors.helpCenterText; // Orange
        case 'summer':
          return colors.cool; // Blue
        case 'autumn':
          return colors.success;
        case 'winter':
          return colors.primary;
        default:
          return colors.defaultUndertone; // Gray
      }
    },
    [colors]
  );

  const pickImage = useCallback(async () => {
    try {
      // Reset state
      setError(null);
      resetUndertone();
      setSkinTone(null); // Reset skin tone when picking new image
      setIsSkinAnalysisSaved(false);
      setHasSavedCurrentAnalysis(false);

      // Request permission
      const { status } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (status !== 'granted') {
        setError('We need camera roll permissions to upload an image.');
        return;
      }

      // Pick image
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        setImage(result.assets[0].uri);

        if (!name) {
          const filename = result.assets[0].uri.split('/').pop();
          const filenameWithoutExt = filename?.split('.')[0];
          setName(filenameWithoutExt || 'My Colour Profile');
        }

        // Start analysis with loading state
        setIsLoading(true);
        setAnalysisProgress(0);
        setError(null);

        try {
          // Simulate progress for better UX
          const progressInterval = setInterval(() => {
            setAnalysisProgress((prev) => {
              if (prev >= 90) {
                if (progressInterval) clearInterval(progressInterval);
                return 90;
              }
              return prev + 10;
            });
          }, 200);

          // Check if services are ready before analysis
          if (!skinUndertoneService.isReady()) {
            await skinUndertoneService.initialize();
          }

          // Analyze skin undertone and skin tone from the selected image
          const undertoneResult = await analyzeImageUndertone(
            result.assets[0].uri
          );
          const skinToneResult = await analyzeImageSkinTone(
            result.assets[0].uri
          );

          // Complete the progress
          setAnalysisProgress(100);
          finishImageAnalysis({
            undertone: undertoneResult,
            skinTone: skinToneResult,
          });

          // Clear progress after a short delay
          setTimeout(() => {
            setAnalysisProgress(0);
          }, 500);
        } catch {
          setError('Failed to analyze image. Please try again.');
          pendingAnalysisResultsRef.current = null;
          setIsLoading(false);
        } finally {
          // Clear any remaining progress interval
          if (typeof setInterval !== 'undefined') {
            setAnalysisProgress(0);
          }
        }
      }
    } catch {
      setError('Failed to pick image. Please try again.');
    }
  }, [
    name,
    resetUndertone,
    analyzeImageUndertone,
    analyzeImageSkinTone,
    finishImageAnalysis,
  ]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find((file) => file.type.startsWith('image/'));

      if (!imageFile) {
        setError('Please drop an image file');
        return;
      }

      try {
        // Reset state
        setError(null);
        resetUndertone();
        setSkinTone(null);
        setIsSkinAnalysisSaved(false);
        setHasSavedCurrentAnalysis(false);

        // Convert file to data URL
        const reader = new FileReader();
        reader.onload = async (event) => {
          const imageUri = event.target?.result as string;
          if (imageUri) {
            setImage(imageUri);

            if (!name) {
              const filename = imageFile.name.split('.')[0];
              setName(filename || 'My Colour Profile');
            }

            // Start analysis with loading state
            setIsLoading(true);
            setAnalysisProgress(0);
            setError(null);

            try {
              // Simulate progress for better UX
              const progressInterval = setInterval(() => {
                setAnalysisProgress((prev) => {
                  if (prev >= 90) {
                    if (progressInterval) clearInterval(progressInterval);
                    return 90;
                  }
                  return prev + 10;
                });
              }, 200);

              // Analyze skin undertone and skin tone from the dropped image
              const undertoneResult = await analyzeImageUndertone(imageUri);
              const skinToneResult = await analyzeImageSkinTone(imageUri);

              // Complete the progress
              setAnalysisProgress(100);
              finishImageAnalysis({
                undertone: undertoneResult,
                skinTone: skinToneResult,
              });

              // Clear progress after a short delay
              setTimeout(() => {
                setAnalysisProgress(0);
              }, 500);
            } catch {
              setError('Failed to analyze image. Please try again.');
              pendingAnalysisResultsRef.current = null;
              setIsLoading(false);
            } finally {
              // Clear any remaining progress interval
              if (typeof setInterval !== 'undefined') {
                setAnalysisProgress(0);
              }
            }
          }
        };
        reader.readAsDataURL(imageFile);
      } catch {
        setError('Failed to process dropped image. Please try again.');
      }
    },
    [
      name,
      resetUndertone,
      analyzeImageUndertone,
      analyzeImageSkinTone,
      finishImageAnalysis,
    ]
  );

  const handleVirtualDrapeTest = () => {
    if (!hasSavedCurrentAnalysis) {
      return;
    }

    trackAnalyticsEvent({
      event: 'button_click',
      button_name: 'Virtual Drape',
      button_location: 'color-analysis-screen-results-section',
      skin_tone: skinTone?.category || 'unknown',
      undertone: undertone || 'unknown',
      has_saved_analysis: hasSavedCurrentAnalysis,
    });

    const virtualDrapeRequestId = Date.now();

    // Always update MainTabs route params so Virtual Drape knows which section to show
    navigation.navigate('MainTabs', {
      initialTab: 'VirtualDrapeTest',
      section: 'virtual-drape-test',
      virtualDrapeView: 'results',
      virtualDrapeViewRequestId: virtualDrapeRequestId,
    });

    // Use onTabPress if available for immediate tab switching in web layout
    if (onTabPress) {
      onTabPress('VirtualDrapeTest');
    }
  };

  // Use AI-enhanced palette when available; keep static palette's makeup recommendations
  const displayPalette: ExtendedColorPalette | null = useMemo(
    () => buildSavePalette(aiPalette, colorPalette),
    [aiPalette, colorPalette]
  );
  const seasonalLabel = seasonPrediction?.subtypeLabel ?? displayPalette?.name;
  const displayCompletePalette =
    (displayPalette as (ColorPalette & { completePalette?: string[] }) | null)
      ?.completePalette ?? [];
  const staticCompletePalette =
    (colorPalette as (ColorPalette & { completePalette?: string[] }) | null)
      ?.completePalette ?? [];
  const recommendedColorsToRender = displayPalette?.colors ?? [];
  const completeColorsToRender =
    displayCompletePalette.length > 0
      ? displayCompletePalette
      : staticCompletePalette;
  const paletteHairColors =
    (displayPalette as (ColorPalette & { hairColors?: string[] }) | null)
      ?.hairColors ??
    (colorPalette as (ColorPalette & { hairColors?: string[] }) | null)
      ?.hairColors ??
    [];
  const paletteEyeColors =
    (displayPalette as (ColorPalette & { eyeColors?: string[] }) | null)
      ?.eyeColors ??
    (colorPalette as (ColorPalette & { eyeColors?: string[] }) | null)
      ?.eyeColors ??
    [];

  const jewelryEntries = useMemo(() => {
    const j =
      (
        displayPalette as
          (ColorPalette & { jewelry?: Record<string, string> }) | null
      )?.jewelry ??
      (
        colorPalette as
          (ColorPalette & { jewelry?: Record<string, string> }) | null
      )?.jewelry;
    if (!j) return [];
    return Object.entries(j).filter(
      ([, hex]) => typeof hex === 'string' && hex.length > 0
    );
  }, [displayPalette, colorPalette]);

  // Fixed chunk size of 10 colours per column (palette strip).
  const paletteColumns = useMemo(() => {
    const displayComplete =
      (displayPalette as (ColorPalette & { completePalette?: string[] }) | null)
        ?.completePalette ?? [];
    const staticComplete =
      (colorPalette as (ColorPalette & { completePalette?: string[] }) | null)
        ?.completePalette ?? [];
    const merged =
      displayComplete.length > 0 ? displayComplete : staticComplete;
    const chunks: string[][] = [];
    for (let i = 0; i < merged.length; i += PALETTE_CHUNK) {
      chunks.push(merged.slice(i, i + PALETTE_CHUNK));
    }
    return chunks;
  }, [displayPalette, colorPalette]);

  useEffect(() => {
    if (paletteColumns.length === 0) {
      setSelectedPaletteColumnIndex(0);
      return;
    }
    setSelectedPaletteColumnIndex((idx) =>
      idx >= paletteColumns.length ? 0 : idx
    );
  }, [paletteColumns.length]);

  const displayedPaletteColumnIndex = Math.min(
    selectedPaletteColumnIndex,
    Math.max(0, paletteColumns.length - 1)
  );
  const selectedPaletteChunk =
    paletteColumns[displayedPaletteColumnIndex] ?? [];

  const renderWardrobeNotification = () => {
    if (!fromWardrobe) return null;

    return (
      <Animated.View
        style={[
          styles.wardrobeNotification,
          {
            backgroundColor: colors.primary,
            padding: isMobile ? 12 : 16,
            marginBottom: isMobile ? 12 : 16,
          },
          { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
        ]}
      >
        <Ionicons name="shirt-outline" size={20} color={colors.background} />
        <Text
          style={[
            styles.wardrobeNotificationText,
            { color: colors.background, fontSize: isMobile ? 13 : 14 },
          ]}
        >
          Complete your colour analysis to start building your smart wardrobe
        </Text>
      </Animated.View>
    );
  };

  const renderUploadSection = () => (
    <Animated.View
      style={[
        styles.uploadSection,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
          borderColor: colors.borderLight,
          paddingHorizontal: isMobile ? 20 : 30,
          paddingVertical: isMobile ? 30 : 40,
          backgroundColor: colors.card,
          ...(Platform.OS === 'web'
            ? {
                boxShadow: `0px 4px 12px 0px ${colors.cardText}1A`,
              }
            : { shadowColor: colors.cardText }),
        },
      ]}
    >
      <Text style={[styles.subtitle, { color: colors.secondary }]}>
        Select or take a photo to analyze and extract dominant colors
      </Text>

      {Platform.OS === 'web' ? (
        <WebUploadContainer
          colors={colors}
          isDragOver={isDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPickImage={pickImage}
          onTakePicture={takePicture}
        />
      ) : (
        <View
          style={[
            styles.uploadContainer,
            {
              borderColor: colors.theme_color,
              width: isMobile ? '100%' : '75%',
              padding: isMobile ? 20 : 30,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.uploadIconContainer}
            onPress={pickImage}
            activeOpacity={0.8}
          >
            <View style={styles.uploadIcon}>
              <Ionicons name="images" size={32} color={colors.primary} />
            </View>
            <Text style={[styles.uploadTitle, { color: colors.text }]}>
              Upload an Image
            </Text>
            <Text style={[styles.uploadText, { color: colors.secondary }]}>
              Choose from Gallery
            </Text>
          </TouchableOpacity>

          <Button
            title="Take Photo"
            onPress={takePicture}
            icon={<Ionicons name="camera" size={20} color={colors.card} />}
            style={styles.cameraButton}
            textStyle={styles.cameraText}
            variant="primary"
            trackClick={true}
            trackingLocation="color-analysis-screen"
            trackingData={{
              button_name: 'Take Photo',
              button_type: 'take_photo',
              screen: 'color_analysis',
            }}
          />
        </View>
      )}
    </Animated.View>
  );

  const renderAnalysisFailure = () => (
    <Animated.View
      style={[
        styles.analysisFailureContainer,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
          backgroundColor: colors.card,
          borderColor: colors.border,
          padding: isMobile ? 20 : 32,
          marginHorizontal: isMobile ? 0 : 16,
        },
      ]}
    >
      <View
        style={[
          styles.analysisFailureIconWrap,
          { backgroundColor: colors.errorBackground },
        ]}
      >
        <Ionicons name="image-outline" size={40} color={colors.errorText} />
      </View>
      <Text style={[styles.analysisFailureTitle, { color: colors.text }]}>
        We couldn&apos;t analyze this photo
      </Text>
      <Text
        style={[styles.analysisFailureMessage, { color: colors.secondary }]}
      >
        ColourSense needs a clear photo of your face to detect skin tone and
        undertone. Try a well-lit selfie with your face centred in the frame.
      </Text>
      <View style={styles.analysisFailureTips}>
        {[
          'Use natural daylight when possible',
          'Face the camera directly',
          'Avoid heavy filters or sunglasses',
        ].map((tip) => (
          <View key={tip} style={styles.analysisFailureTipRow}>
            <Ionicons
              name="checkmark-circle"
              size={16}
              color={colors.primary}
            />
            <Text
              style={[styles.analysisFailureTipText, { color: colors.text }]}
            >
              {tip}
            </Text>
          </View>
        ))}
      </View>
      {image && (
        <Image
          source={{ uri: image }}
          style={[
            styles.analysisFailurePreview,
            { borderColor: colors.border },
          ]}
        />
      )}
      <View style={styles.analysisFailureActions}>
        <Button
          title="Try Another Photo"
          onPress={resetImage}
          variant="primary"
          style={styles.analysisFailurePrimaryBtn}
          icon={<Ionicons name="images" size={18} color={colors.card} />}
          trackClick={true}
          trackingLocation="color-analysis-screen"
          trackingData={{
            button_name: 'Try Another Photo',
            button_type: 'analysis_failure_retry',
            screen: 'color_analysis',
          }}
        />
        <Button
          title="Take a Photo"
          onPress={takePicture}
          variant="outline"
          style={styles.analysisFailureSecondaryBtn}
          icon={<Ionicons name="camera" size={18} color={colors.primary} />}
        />
      </View>
    </Animated.View>
  );

  const renderImagePreview = () => (
    <Animated.View
      style={[
        styles.imagePreviewContainer,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
          marginHorizontal: 0,
        },
      ]}
    >
      <View
        style={[
          styles.dashboardRow,
          {
            flexDirection: isMobile ? 'column' : 'row',
            paddingHorizontal: isMobile
              ? Math.max(12, containerPadding)
              : Platform.OS === 'web'
                ? 4
                : 0,
          },
        ]}
      >
        {/* Left: profile + stacked actions */}
        <View
          style={[
            styles.dashboardColLeft,
            isMobile
              ? styles.dashboardColFullWidth
              : styles.dashboardColLeftFixed,
          ]}
        >
          <View
            style={[
              styles.dashboardCard,
              styles.profileCard,
              {
                borderColor: colors.border,
                backgroundColor: colors.card,
              },
            ]}
          >
            <Image
              source={{ uri: image! }}
              style={[
                styles.imagePreview,
                !isMobile && styles.profileImage,
                isMobile && mobileProfileImageWidth != null
                  ? {
                      alignSelf: 'center',
                      width: mobileProfileImageWidth,
                    }
                  : !isMobile && { width: '100%' },
                Platform.OS === 'web' && !isMobile && { maxWidth: '100%' },
              ]}
            />
            {!isPublicPreview && (
              <View style={[styles.imageActions, { marginTop: 12 }]}>
                <Button
                  title="Start Over"
                  onPress={resetImage}
                  style={[styles.changeImageButton, styles.dashboardActionBtn]}
                  variant="outline"
                  icon={
                    <Ionicons name="refresh" size={16} color={colors.primary} />
                  }
                  hoverColor={colors.authBackground}
                />
              </View>
            )}
          </View>

          <View style={styles.dashboardActionStack}>
            {fromWardrobe && (
              <Button
                title="Continue to Wardrobe"
                onPress={analyzeColors}
                loading={isLoading}
                style={[
                  styles.primaryButton,
                  styles.dashboardActionBtn,
                  {
                    backgroundColor: colors.primary,
                    shadowColor: colors.primary,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 8,
                  },
                ]}
                trackClick={true}
                trackingLocation="color-analysis-screen"
                trackingData={{
                  button_name: 'Continue to Wardrobe',
                  button_type: 'continue_to_wardrobe',
                  screen: 'color_analysis',
                }}
              />
            )}
            {skinTone &&
              undertone &&
              skinTone.category !== 'Unknown' &&
              !isPublicPreview && (
                <View
                  style={[
                    styles.dashboardActionStack,
                    { paddingHorizontal: isMobile ? 20 : 'auto' },
                  ]}
                >
                  <Button
                    title="Save Analysis"
                    onPress={handleManualSave}
                    variant="primary"
                    icon={
                      <Ionicons
                        name="save"
                        size={16}
                        color={colors.background}
                      />
                    }
                    hoverColor={colors.primary}
                    disabled={isLoading || !skinTone || !undertone}
                    style={styles.dashboardActionBtn}
                    trackClick={true}
                    trackingLocation="color-analysis-screen"
                    trackingData={{
                      button_name: 'Save Analysis',
                      button_type: 'save_analysis',
                      screen: 'color_analysis',
                    }}
                  />
                  <Button
                    title="Virtual Drape"
                    onPress={handleVirtualDrapeTest}
                    variant="primary"
                    icon={
                      <Ionicons
                        name="shirt-outline"
                        size={16}
                        color={colors.background}
                      />
                    }
                    hoverColor={colors.primary}
                    disabled={
                      isLoading ||
                      !skinTone ||
                      !undertone ||
                      !hasSavedCurrentAnalysis
                    }
                    style={styles.dashboardActionBtn}
                    trackClick={true}
                    trackingLocation="color-analysis-screen"
                    trackingData={{
                      button_name: 'Virtual Drape from Colour Analysis Result',
                      button_type: 'virtual_drape_from_colour_analysis_result',
                      screen: 'color_analysis',
                    }}
                  />
                </View>
              )}
          </View>
        </View>

        {/* Middle: summary + perfect matches */}
        <View
          style={[
            styles.dashboardColMiddle,
            isMobile ? styles.dashboardColFullWidth : null,
          ]}
        >
          {isPublicPreview ? (
            <BlurredLockOverlay
              message="Sign up to unlock your colour analysis!"
              blurAmount={5}
              overlayOpacity={0.55}
              style={styles.publicPreviewResults}
            >
              <View
                style={[
                  styles.dashboardCard,
                  {
                    borderColor: colors.border,
                    backgroundColor: colors.card,
                    paddingHorizontal: isMobile ? 40 : 'auto',
                  },
                ]}
              >
                {skinTone && (
                  <View style={[styles.summarySection]}>
                    <Text
                      style={[
                        styles.summaryKicker,
                        { color: colors.secondary },
                      ]}
                    >
                      SKIN TONE
                    </Text>
                    <View style={styles.skinToneInfo}>
                      <View
                        style={[
                          styles.colorSwatch,
                          {
                            backgroundColor: skinTone.color,
                            borderColor: colors.borderLight,
                          },
                        ]}
                      />
                      <View style={styles.skinToneDetails}>
                        <Text
                          style={[
                            styles.skinToneCategory,
                            { color: colors.text },
                          ]}
                        >
                          {skinTone.category}
                        </Text>
                        <Text
                          style={[
                            styles.skinToneConfidence,
                            { color: colors.secondary },
                          ]}
                        >
                          Confidence: {Math.round(skinTone.confidence * 100)}%
                        </Text>
                      </View>
                    </View>
                    {skinTone.category === 'Unknown' && (
                      <View
                        style={[
                          styles.unknownSkinToneMessage,
                          { marginTop: 12 },
                        ]}
                      >
                        <Ionicons
                          name="information-circle"
                          size={16}
                          color={colors.secondary}
                        />
                        <Text
                          style={[
                            styles.unknownSkinToneText,
                            { color: colors.secondary },
                          ]}
                        >
                          Unable to detect skin tone. Please try a clearer photo
                          with better lighting.
                        </Text>
                      </View>
                    )}
                  </View>
                )}
                {undertone && skinTone && skinTone.category !== 'Unknown' && (
                  <>
                    <View
                      style={[
                        styles.summaryDivider,
                        { backgroundColor: colors.border },
                      ]}
                    />
                    <View style={styles.summarySection}>
                      <Text
                        style={[
                          styles.summaryKicker,
                          { color: colors.secondary },
                        ]}
                      >
                        SEASON
                      </Text>
                      <View style={styles.undertoneSection}>
                        <View
                          style={[
                            styles.undertoneChip,
                            {
                              backgroundColor: getUndertoneColor(undertone),
                            },
                          ]}
                        >
                          <Text style={styles.undertoneChipText}>
                            {seasonalLabel || 'Analyzing...'}
                          </Text>
                        </View>
                      </View>
                      {undertoneLoading && (
                        <Text
                          style={[
                            styles.loadingText,
                            { color: colors.secondary },
                          ]}
                        >
                          Building Your Palette
                        </Text>
                      )}
                      {undertoneError && (
                        <Text
                          style={[
                            styles.errorText,
                            { color: colors.errorText },
                          ]}
                        >
                          {undertoneError}
                        </Text>
                      )}
                    </View>
                  </>
                )}
                {displayPalette &&
                  skinTone &&
                  skinTone.category !== 'Unknown' && (
                    <>
                      <View
                        style={[
                          styles.summaryDivider,
                          { backgroundColor: colors.border },
                        ]}
                      />
                      <View style={styles.summarySection}>
                        <Text
                          style={[
                            styles.summaryKicker,
                            { color: colors.secondary },
                          ]}
                        >
                          ABOUT YOUR PALETTE
                        </Text>
                        <Text
                          style={[
                            styles.paletteDescription,
                            { color: colors.secondary, width: '100%' },
                          ]}
                        >
                          {displayPalette.description}
                        </Text>
                      </View>
                    </>
                  )}
              </View>
            </BlurredLockOverlay>
          ) : (
            <View
              style={[
                styles.dashboardCard,
                {
                  borderColor: colors.border,
                  backgroundColor: colors.card,
                  paddingHorizontal: isMobile ? 40 : 'auto',
                },
              ]}
            >
              {skinTone && (
                <View style={styles.summarySection}>
                  <Text
                    style={[styles.summaryKicker, { color: colors.secondary }]}
                  >
                    SKIN TONE
                  </Text>
                  <View style={styles.skinToneInfo}>
                    <View
                      style={[
                        styles.colorSwatch,
                        {
                          backgroundColor: skinTone.color,
                          borderColor: colors.borderLight,
                        },
                      ]}
                    />
                    <View style={styles.skinToneDetails}>
                      <Text
                        style={[
                          styles.skinToneCategory,
                          { color: colors.text },
                        ]}
                      >
                        {skinTone.category}
                      </Text>
                      <Text
                        style={[
                          styles.skinToneConfidence,
                          { color: colors.secondary },
                        ]}
                      >
                        Confidence: {Math.round(skinTone.confidence * 100)}%
                      </Text>
                    </View>
                  </View>
                  {skinTone.category === 'Unknown' && (
                    <View
                      style={[styles.unknownSkinToneMessage, { marginTop: 12 }]}
                    >
                      <Ionicons
                        name="information-circle"
                        size={16}
                        color={colors.secondary}
                      />
                      <Text
                        style={[
                          styles.unknownSkinToneText,
                          { color: colors.secondary },
                        ]}
                      >
                        Unable to detect skin tone. Please try a clearer photo
                        with better lighting.
                      </Text>
                    </View>
                  )}
                </View>
              )}
              {undertone && skinTone && skinTone.category !== 'Unknown' && (
                <>
                  <View
                    style={[
                      styles.summaryDivider,
                      { backgroundColor: colors.border },
                    ]}
                  />
                  <View style={styles.summarySection}>
                    <Text
                      style={[
                        styles.summaryKicker,
                        { color: colors.secondary },
                      ]}
                    >
                      SEASON
                    </Text>
                    <View style={styles.undertoneSection}>
                      <View
                        style={[
                          styles.undertoneChip,
                          { backgroundColor: getUndertoneColor(undertone) },
                        ]}
                      >
                        <Text style={styles.undertoneChipText}>
                          {seasonalLabel || 'Analyzing...'}
                        </Text>
                      </View>
                    </View>
                    {(undertoneLoading || isAIPaletteLoading) && (
                      <Text
                        style={[
                          styles.loadingText,
                          { color: colors.secondary },
                        ]}
                      />
                    )}
                    {undertoneError && (
                      <Text
                        style={[styles.errorText, { color: colors.errorText }]}
                      >
                        {undertoneError}
                      </Text>
                    )}
                  </View>
                </>
              )}
              {displayPalette &&
                skinTone &&
                skinTone.category !== 'Unknown' && (
                  <>
                    <View
                      style={[
                        styles.summaryDivider,
                        { backgroundColor: colors.border },
                      ]}
                    />
                    <View style={styles.summarySection}>
                      <Text
                        style={[
                          styles.summaryKicker,
                          { color: colors.secondary },
                        ]}
                      >
                        ABOUT YOUR PALETTE
                      </Text>
                      <Text
                        style={[
                          styles.paletteDescription,
                          { color: colors.secondary, width: '100%' },
                        ]}
                      >
                        {displayPalette.description}
                      </Text>
                    </View>
                  </>
                )}
            </View>
          )}

          {displayPalette &&
            skinTone &&
            skinTone.category !== 'Unknown' &&
            (isPublicPreview ? (
              <BlurredLockOverlay
                message="Sign up to unlock your recommended colors!"
                blurAmount={5}
                overlayOpacity={0.55}
                style={{
                  ...styles.dashboardCard,
                  ...styles.matchesCard,
                  backgroundColor: colors.card,
                  borderColor: colors.border,
                }}
              >
                <View style={styles.matchesCardHeader}>
                  <Text
                    style={[
                      styles.sectionTitle,
                      { color: colors.text, marginBottom: 0 },
                    ]}
                  >
                    Your perfect colour matches
                  </Text>
                  <Text
                    style={[
                      styles.colorCountBadge,
                      { color: colors.secondary },
                    ]}
                  >
                    {recommendedColorsToRender.length} colors
                  </Text>
                </View>
                <View style={styles.matchesGridFive}>
                  {recommendedColorsToRender.map((color, index) => (
                    <View key={index} style={styles.matchesSwatchItem}>
                      <View
                        style={[
                          styles.colorSwatchBox,
                          {
                            backgroundColor: color,
                            borderColor: colors.borderLight,
                          },
                        ]}
                      />
                    </View>
                  ))}
                </View>
              </BlurredLockOverlay>
            ) : (
              <View
                style={[
                  styles.dashboardCard,
                  styles.matchesCard,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                    paddingHorizontal: isMobile ? 40 : 'auto',
                  },
                ]}
              >
                <View style={styles.matchesCardHeader}>
                  <Text
                    style={[
                      styles.sectionTitle,
                      { color: colors.text, marginBottom: 0 },
                    ]}
                  >
                    Your perfect colour matches
                  </Text>
                  <Text
                    style={[
                      styles.colorCountBadge,
                      { color: colors.secondary },
                    ]}
                  >
                    {recommendedColorsToRender.length} colors
                  </Text>
                </View>
                <View style={styles.matchesGridFive}>
                  {recommendedColorsToRender.map((color, index) => (
                    <View key={index} style={styles.matchesSwatchItem}>
                      <View
                        style={[
                          styles.colorSwatchBox,
                          {
                            backgroundColor: color,
                            borderColor: colors.borderLight,
                          },
                        ]}
                      />
                    </View>
                  ))}
                </View>
              </View>
            ))}
        </View>

        {/* Right: jewelry, hair, eye, full palette columns */}
        <View
          style={[
            styles.dashboardColRight,
            isMobile ? styles.dashboardColFullWidth : null,
            isMobile && !isPublicPreview ? { marginTop: 310 } : null,
          ]}
        >
          <View
            style={[
              styles.dashboardCard,
              styles.tallPaletteCard,
              {
                borderColor: colors.border,
                backgroundColor: colors.card,
              },
            ]}
          >
            {(jewelryEntries.length > 0 ||
              paletteHairColors.length > 0 ||
              paletteEyeColors.length > 0) &&
              skinTone &&
              skinTone.category !== 'Unknown' &&
              (isPublicPreview ? (
                <BlurredLockOverlay
                  message="Sign up to unlock hair and eye colour recommendations!"
                  blurAmount={5}
                  overlayOpacity={0.55}
                  style={{ width: '100%' }}
                >
                  <View style={styles.personalSwatchesBlock}>
                    {jewelryEntries.length > 0 && (
                      <View style={styles.personalRow}>
                        <Text
                          style={[
                            styles.summaryKicker,
                            { color: colors.secondary },
                          ]}
                        >
                          JEWELRY
                        </Text>
                        <View style={styles.jewelryRow}>
                          {jewelryEntries.map(([key, hex]) => (
                            <View key={key} style={styles.jewelrySwatchWrap}>
                              <JewelryMetalSwatch
                                jewelryKey={key}
                                hex={hex}
                                style={styles.jewelrySwatch}
                                borderColor={colors.borderLight}
                              />
                            </View>
                          ))}
                        </View>
                      </View>
                    )}
                    {paletteHairColors.length > 0 && (
                      <View style={styles.personalRow}>
                        <Text
                          style={[styles.makeupTitle, { color: colors.text }]}
                        >
                          Hair Colors
                        </Text>
                        <View
                          style={[
                            styles.makeupGrid,
                            isMobile && styles.makeupGridMobile,
                            { marginBottom: 12 },
                          ]}
                        >
                          {paletteHairColors.map((color, index) => (
                            <View
                              key={`hair-${index}`}
                              style={[
                                styles.makeupItem,
                                isMobile && styles.makeupItemMobile,
                              ]}
                            >
                              <View
                                style={[
                                  styles.makeupColorSwatch,
                                  {
                                    backgroundColor: color,
                                    borderColor: colors.borderLight,
                                  },
                                ]}
                              />
                            </View>
                          ))}
                        </View>
                      </View>
                    )}
                    {paletteEyeColors.length > 0 && (
                      <View style={styles.personalRow}>
                        <Text
                          style={[styles.sectionTitle, { color: colors.text }]}
                        >
                          Eye Colors
                        </Text>
                        <View
                          style={[
                            styles.makeupGrid,
                            isMobile && styles.makeupGridMobile,
                          ]}
                        >
                          {paletteEyeColors.map((color, index) => (
                            <View
                              key={`eye-${index}`}
                              style={[
                                styles.makeupItem,
                                isMobile && styles.makeupItemMobile,
                              ]}
                            >
                              <View
                                style={[
                                  styles.makeupColorSwatch,
                                  {
                                    backgroundColor: color,
                                    borderColor: colors.borderLight,
                                  },
                                ]}
                              />
                            </View>
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
                </BlurredLockOverlay>
              ) : (
                <View
                  style={[
                    styles.personalSwatchesBlock,
                    { paddingHorizontal: isMobile ? 22 : 'auto' },
                  ]}
                >
                  {jewelryEntries.length > 0 && (
                    <View style={styles.personalRow}>
                      <Text
                        style={[
                          styles.summaryKicker,
                          { color: colors.secondary },
                        ]}
                      >
                        JEWELRY
                      </Text>
                      <View style={styles.jewelryRow}>
                        {jewelryEntries.map(([key, hex]) => (
                          <View key={key} style={styles.jewelrySwatchWrap}>
                            <JewelryMetalSwatch
                              jewelryKey={key}
                              hex={hex}
                              style={styles.jewelrySwatch}
                              borderColor={colors.borderLight}
                            />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                  {paletteHairColors.length > 0 && (
                    <View style={styles.personalRow}>
                      <Text
                        style={[styles.makeupTitle, { color: colors.text }]}
                      >
                        Hair Colors
                      </Text>
                      <View
                        style={[
                          styles.makeupGrid,
                          isMobile && styles.makeupGridMobile,
                          { marginBottom: 12 },
                        ]}
                      >
                        {paletteHairColors.map((color, index) => (
                          <View
                            key={`hair-${index}`}
                            style={[
                              styles.makeupItem,
                              isMobile && styles.makeupItemMobile,
                            ]}
                          >
                            <View
                              style={[
                                styles.makeupColorSwatch,
                                {
                                  backgroundColor: color,
                                  borderColor: colors.borderLight,
                                },
                              ]}
                            />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                  {paletteEyeColors.length > 0 && (
                    <View style={styles.personalRow}>
                      <Text
                        style={[styles.sectionTitle, { color: colors.text }]}
                      >
                        Eye Colors
                      </Text>
                      <View
                        style={[
                          styles.makeupGrid,
                          isMobile && styles.makeupGridMobile,
                        ]}
                      >
                        {paletteEyeColors.map((color, index) => (
                          <View
                            key={`eye-${index}`}
                            style={[
                              styles.makeupItem,
                              isMobile && styles.makeupItemMobile,
                            ]}
                          >
                            <View
                              style={[
                                styles.makeupColorSwatch,
                                {
                                  backgroundColor: color,
                                  borderColor: colors.borderLight,
                                },
                              ]}
                            />
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                </View>
              ))}

            {completeColorsToRender.length > 0 &&
              displayPalette &&
              skinTone &&
              skinTone.category !== 'Unknown' && (
                <>
                  <View
                    style={[
                      styles.summaryDivider,
                      { backgroundColor: colors.border, marginVertical: 12 },
                    ]}
                  />
                  {isPublicPreview ? (
                    <BlurredLockOverlay
                      message="Sign up to unlock your complete colour palette!"
                      blurAmount={5}
                      overlayOpacity={0.55}
                      style={{ width: '100%' }}
                    >
                      <View style={styles.fullPaletteHeaderRow}>
                        <Text
                          style={[
                            styles.sectionTitle,
                            { color: colors.text, marginBottom: 0 },
                          ]}
                        >
                          Full palette collection
                        </Text>
                        <Text
                          style={[
                            styles.colorCountBadge,
                            { color: colors.secondary },
                          ]}
                        >
                          {paletteColumns.length} palettes
                        </Text>
                      </View>
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.paletteColumnsScroll}
                        contentContainerStyle={
                          styles.paletteColumnsScrollContent
                        }
                      >
                        <View style={styles.paletteColumnsRow}>
                          {paletteColumns.map((column, colIdx) => (
                            <View
                              key={`pc-${colIdx}`}
                              style={styles.paletteColumnCell}
                            >
                              <Text
                                style={[
                                  styles.paletteColumnIndex,
                                  { color: colors.secondary },
                                ]}
                              >
                                {colIdx + 1}
                              </Text>
                              {renderPaletteColumnSwatches(
                                column,
                                colIdx,
                                colors.borderLight
                              )}
                            </View>
                          ))}
                        </View>
                      </ScrollView>
                    </BlurredLockOverlay>
                  ) : (
                    <>
                      <View
                        style={[
                          styles.fullPaletteHeaderRow,
                          { paddingHorizontal: isMobile ? 22 : 'auto' },
                        ]}
                      >
                        <Text
                          style={[
                            styles.sectionTitle,
                            { color: colors.text, marginBottom: 0 },
                          ]}
                        >
                          Full palette collection
                        </Text>
                        <Text
                          style={[
                            styles.colorCountBadge,
                            { color: colors.secondary },
                          ]}
                        >
                          {paletteColumns.length} palettes
                        </Text>
                      </View>
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.paletteColumnsScroll}
                        contentContainerStyle={
                          styles.paletteColumnsScrollContent
                        }
                      >
                        <View style={styles.paletteColumnsRow}>
                          {paletteColumns.map((column, colIdx) => (
                            <TouchableOpacity
                              key={`pc-${colIdx}`}
                              activeOpacity={0.85}
                              onPress={() =>
                                setSelectedPaletteColumnIndex(colIdx)
                              }
                              style={[
                                styles.paletteColumnCell,
                                {
                                  borderColor:
                                    displayedPaletteColumnIndex === colIdx
                                      ? colors.primary
                                      : colors.borderLight,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.paletteColumnIndex,
                                  { color: colors.secondary },
                                ]}
                              >
                                {colIdx + 1}
                              </Text>
                              {renderPaletteColumnSwatches(
                                column,
                                colIdx,
                                colors.borderLight
                              )}
                            </TouchableOpacity>
                          ))}
                        </View>
                      </ScrollView>
                      {paletteColumns.length > 0 && (
                        <View
                          style={[
                            styles.chunkFooter,
                            isMobile ? { marginBottom: 70 } : null,
                            { borderColor: colors.borderLight },
                          ]}
                        >
                          <Text
                            style={[
                              styles.chunkFooterTitle,
                              { color: colors.text },
                            ]}
                          >
                            Palette {displayedPaletteColumnIndex + 1}
                          </Text>
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            style={styles.chunkFooterSwatches}
                          >
                            <View style={styles.chunkFooterRow}>
                              {Array.from({ length: PALETTE_CHUNK }, (_, i) => {
                                const color = selectedPaletteChunk[i];
                                const hasColor =
                                  typeof color === 'string' && color.length > 0;

                                return (
                                  <View
                                    key={`footer-${i}`}
                                    style={[
                                      styles.chunkFooterSwatch,
                                      {
                                        backgroundColor: hasColor
                                          ? color
                                          : 'transparent',
                                        borderColor: hasColor
                                          ? colors.borderLight
                                          : 'transparent',
                                      },
                                    ]}
                                  />
                                );
                              })}
                            </View>
                          </ScrollView>
                        </View>
                      )}
                    </>
                  )}
                </>
              )}
          </View>
        </View>
      </View>
    </Animated.View>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for premium upsell section
  const renderPremiumUpsell = () => {
    if (isPremium || !hasCompletedAnalysis) return null;

    return (
      <Animated.View
        style={[
          styles.premiumUpsell,
          {
            backgroundColor: colors.card,
            borderColor: colors.border,
            marginTop: isMobile ? 16 : 24,
            padding: isMobile ? 16 : 20,
          },
          { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
        ]}
      >
        <View style={styles.premiumHeader}>
          <Ionicons name="sparkles" size={24} color={colors.primary} />
          <Text style={[styles.premiumTitle, { color: colors.text }]}>
            Unlock Premium Features
          </Text>
        </View>

        <Text
          style={[
            styles.premiumDescription,
            { color: colors.secondary, fontSize: isMobile ? 13 : 14 },
          ]}
        >
          Get unlimited colour analysis, wardrobe organization, and personalized
          outfit recommendations.
        </Text>

        <View style={styles.premiumFeatures}>
          <View style={styles.premiumFeature}>
            <Ionicons
              name="checkmark-circle"
              size={16}
              color={colors.primary}
            />
            <Text style={[styles.premiumFeatureText, { color: colors.text }]}>
              Unlimited colour analysis
            </Text>
          </View>
          <View style={styles.premiumFeature}>
            <Ionicons
              name="checkmark-circle"
              size={16}
              color={colors.primary}
            />
            <Text style={[styles.premiumFeatureText, { color: colors.text }]}>
              Smart wardrobe organization
            </Text>
          </View>
          <View style={styles.premiumFeature}>
            <Ionicons
              name="checkmark-circle"
              size={16}
              color={colors.primary}
            />
            <Text style={[styles.premiumFeatureText, { color: colors.text }]}>
              AI outfit recommendations
            </Text>
          </View>
        </View>

        {/* Upgrade button commented out for now */}
        {/* <Button
          title='Upgrade to Premium'
          onPress={handlePremiumUpgrade}
          style={styles.premiumButton}
          variant='primary'
          trackClick={true}
        /> */}
      </Animated.View>
    );
  };

  const fabBottomOffset = bottomNavHeight + 16 + insets.bottom;
  const fabRightOffset = 16 + insets.right;
  const modalSkinToneData = skinUndertoneService.getSkinToneData();
  const modalSkinToneCategory =
    skinTone?.category || modalSkinToneData?.skinToneCategory || undefined;
  const modalDominantColors =
    modalSkinToneData?.dominantColors?.map((c) => ({ hex: c.hex })) ??
    undefined;
  const showPremiumInsightsFab =
    ENABLE_AI_FEATURES &&
    !isPublicPreview &&
    !!image &&
    !!undertone &&
    !!skinTone &&
    skinTone.category !== 'Unknown' &&
    !isLoading;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Toast notifications */}
      {isSkinAnalysisSaved && (
        <Animated.View
          style={[
            styles.toastContainer,
            {
              transform: [{ translateY: toastAnim }],
            },
          ]}
        >
          <View
            style={[styles.toastContent, { backgroundColor: colors.success }]}
          >
            <Ionicons
              name="checkmark-circle"
              size={20}
              color={colors.background}
            />
            <Text
              style={[
                styles.toastText,
                { color: colors.onPrimary ?? colors.background },
              ]}
            >
              Skin analysis updated successfully!
            </Text>
          </View>
        </Animated.View>
      )}

      {error && (
        <Animated.View
          style={[
            styles.toastContainer,
            {
              transform: [{ translateY: errorToastAnim }],
            },
          ]}
        >
          <View
            style={[
              styles.toastContent,
              { backgroundColor: colors.errorBackground },
            ]}
          >
            <Ionicons name="alert-circle" size={20} color={colors.errorText} />
            <Text style={[styles.toastText, { color: colors.errorText }]}>
              {error}
            </Text>
          </View>
        </Animated.View>
      )}

      <ScrollView
        style={[
          styles.scrollView,
          {
            // Web desktop: modest gutter; mobile adds a bit more side inset than the hook alone
            paddingHorizontal: isMobile
              ? isPublicPreview
                ? Math.max(8, containerPadding - 8)
                : containerPadding + mobileScrollOuterExtra
              : containerPadding,
          },
        ]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            ...(isMobile
              ? {
                  paddingHorizontal: mobileScrollContentPaddingH,
                  paddingTop: 16,
                  paddingBottom: bottomNavHeight + 24,
                }
              : {
                  padding: 0,
                  paddingTop: 24,
                }),
            minWidth: !isMobile ? 800 : null,
            alignSelf: 'center',
            alignItems: isMobile ? 'center' : 'stretch',
            justifyContent: !isMobile
              ? 'flex-start'
              : image
                ? 'flex-start'
                : 'center',
            minHeight: isMobile ? availableHeight : undefined,
            flexGrow: 1,
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {renderWardrobeNotification()}

        {(!image || isLoading || undertoneLoading) && (
          <PageHero
            title="Colour Analysis"
            subtitle="Upload a photo to discover your palette and best colours."
          />
        )}

        {image ? (
          <>
            {(isLoading || undertoneLoading) && (
              <View
                style={[
                  styles.progressContainer,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                    margin: isMobile ? 12 : 16,
                    padding: isMobile ? 16 : 20,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.progressText,
                    { color: colors.text, fontSize: isMobile ? 14 : 16 },
                  ]}
                >
                  Building Your Palette
                </Text>
                <View
                  style={[
                    styles.progressBar,
                    { backgroundColor: colors.border },
                  ]}
                >
                  <Animated.View
                    style={[
                      styles.progressFill,
                      {
                        backgroundColor: colors.primary,
                        width: `${analysisProgress}%`,
                      },
                    ]}
                  />
                </View>
              </View>
            )}
            {!isLoading &&
              !undertoneLoading &&
              showAnalysisFailure &&
              renderAnalysisFailure()}
            {!isLoading &&
              !undertoneLoading &&
              !showAnalysisFailure &&
              renderImagePreview()}
          </>
        ) : isCameraMode ? (
          <>
            <View
              style={[styles.realTimeHeader, { backgroundColor: colors.card }]}
            >
              <View style={styles.realTimeContent}>
                <Ionicons
                  name="color-palette"
                  size={24}
                  color={colors.primary}
                />
                <Text style={[styles.realTimeTitle, { color: colors.text }]}>
                  Real-time Colour Testing
                </Text>
              </View>
              <Text
                style={[styles.realTimeSubtitle, { color: colors.secondary }]}
              >
                For best results, use natural daylight, face a window, and
                remove your glasses to reduce glare.
              </Text>
            </View>
            <View
              style={[styles.cameraWrapper, { width: isMobile ? 400 : 400 }]}
            >
              <CameraComponent
                onImageCaptured={async (uri, detectedSkinTone) => {
                  setImage(uri);
                  setSkinTone(detectedSkinTone || null);
                  setIsCameraMode(false);
                  setIsLoading(true);
                  setAnalysisProgress(0);
                  setError(null);

                  try {
                    const undertoneResult = await analyzeImageUndertone(uri);
                    const skinToneResult =
                      (await analyzeImageSkinTone(uri)) ??
                      detectedSkinTone ??
                      null;
                    setAnalysisProgress(100);
                    finishImageAnalysis({
                      undertone: undertoneResult,
                      skinTone: skinToneResult,
                    });
                  } catch {
                    setError('Failed to analyze image. Please try again.');
                    pendingAnalysisResultsRef.current = null;
                    setIsLoading(false);
                  } finally {
                    setAnalysisProgress(0);
                  }
                }}
                onClose={() => setIsCameraMode(false)}
              />
            </View>
          </>
        ) : (
          renderUploadSection()
        )}

        {/* {renderPremiumUpsell()} */}
      </ScrollView>

      <ColorAnalysisWizardModal
        visible={showWizardModal}
        onClose={() => setShowWizardModal(false)}
      />

      {showPremiumInsightsFab &&
        !aiInsightsModalVisible &&
        (Platform.OS === 'web' ? (
          <FloatingActionButton
            bottom={fabBottomOffset}
            right={fabRightOffset}
            onPress={() => {
              trackButtonClick(
                'AI insights FAB',
                'color-analysis-screen-ai-insights-fab'
              );
              setAiInsightsModalVisible(true);
            }}
          />
        ) : (
          <FloatingActionButtonOverlay>
            <FloatingActionButton
              viewportFixed={false}
              bottom={fabBottomOffset}
              right={fabRightOffset}
              onPress={() => {
                trackButtonClick(
                  'AI insights FAB',
                  'color-analysis-screen-ai-insights-fab'
                );
                setAiInsightsModalVisible(true);
              }}
            />
          </FloatingActionButtonOverlay>
        ))}

      {ENABLE_AI_FEATURES && (
        <PremiumAIColorChatModal
          visible={aiInsightsModalVisible}
          onClose={() => setAiInsightsModalVisible(false)}
          fabBottom={fabBottomOffset}
          fabRight={fabRightOffset}
          promptSectionKeys={['color_analysis']}
          detectedSeason={undertone}
          undertone={undertone ? legacyUndertoneForApi(undertone) : undefined}
          skinToneCategory={modalSkinToneCategory}
          dominantColors={modalDominantColors}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  shareButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },

  scrollView: {
    flex: 1,
  },

  scrollContent: {
    padding: 24,
    paddingTop: 0,
    width: '100%',
    flexGrow: 1,
  },

  wardrobeNotification: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
    gap: 8,
  },

  wardrobeNotificationText: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  uploadSection: {
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 30,
    paddingVertical: 40,
  },

  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 3,
    marginTop: 10,
    textAlign: 'center',
  },

  subtitle: {
    fontSize: 14,
    marginBottom: 25,
    textAlign: 'center',
  },

  uploadContainer: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 16,
    padding: 30,
    marginBottom: 24,
    width: '75%',
    alignSelf: 'center',
    minHeight: 200,
  },

  uploadTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
  },

  uploadIconContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },

  uploadIcon: {
    width: 64,
    height: 64,
    justifyContent: 'center',
    alignItems: 'center',
  },

  uploadText: {
    fontSize: 13,
  },

  dragDropText: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },

  cameraButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    width: '100%',
    justifyContent: 'center',
  },

  cameraText: {
    fontSize: 16,
    fontWeight: '500',
  },

  publicPreviewBlur: {
    marginBottom: 24,
  },
  publicPreviewResults: {
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
  },
  imagePreviewContainer: {
    alignItems: 'stretch',
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: 1600,
    marginBottom: 24,
    gap: 20,
  },

  imagePreviewResults: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    padding: 16,
    gap: 10,
  },

  dashboardRow: {
    width: '100%',
    alignItems: 'stretch',
    gap: 16,
    paddingHorizontal: Platform.OS === 'web' ? 4 : 0,
  },

  dashboardColLeft: {
    gap: 16,
  },

  dashboardColLeftFixed: {
    width: 260,
    flexShrink: 0,
  },

  dashboardColMiddle: {
    flex: 1,
    minWidth: 0,
    gap: 16,
  },

  dashboardColRight: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    gap: 16,
  },

  dashboardColFullWidth: {
    width: '100%',
    alignSelf: 'stretch',
  },

  dashboardCard: {
    borderWidth: 1,
    borderRadius: 14,
    padding: 16,
    width: '100%',
  },

  profileCard: {
    alignItems: 'stretch',
  },

  profileImage: {
    marginBottom: 0,
    alignSelf: 'stretch',
  },

  dashboardActionStack: {
    width: '100%',
    gap: 10,
  },

  dashboardActionBtn: {
    alignSelf: 'stretch',
    width: '100%',
  },

  summarySection: {
    width: '100%',
    paddingVertical: 4,
  },

  summaryKicker: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    marginBottom: 10,
    textTransform: 'uppercase',
  },

  summaryDivider: {
    height: StyleSheet.hairlineWidth,
    width: '100%',
    marginVertical: 4,
  },

  matchesCard: {
    marginTop: 0,
  },

  matchesCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 8,
  },

  matchesGridFive: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 10 : 8,
    justifyContent: 'flex-start',
    width: '100%',
  },

  matchesSwatchItem: {
    width: '18%',
    maxWidth: 56,
    minWidth: 36,
    aspectRatio: 1,
  },

  colorCountBadge: {
    fontSize: 12,
    fontWeight: '600',
  },

  tallPaletteCard: {
    width: '100%',
    minHeight: 200,
  },

  personalSwatchesBlock: {
    width: '100%',
    gap: 16,
  },

  personalRow: {
    width: '100%',
  },

  jewelryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 8,
    alignItems: 'center',
  },

  jewelrySwatchWrap: {
    marginRight: 0,
  },

  jewelrySwatch: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
  },

  fullPaletteHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    gap: 8,
  },

  paletteColumnsScroll: {
    maxWidth: '100%',
  },

  paletteColumnsScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  paletteColumnsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 8,
    paddingBottom: 4,
  },

  paletteColumnCell: {
    minWidth: 36,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    gap: 5,
  },

  paletteColumnIndex: {
    fontSize: 11,
    fontWeight: '700',
    marginBottom: 2,
  },

  paletteColumnSwatch: {
    width: 26,
    height: 26,
    borderRadius: 6,
    borderWidth: 1,
  },

  paletteColumnSwatchSpacer: {
    width: 26,
    height: 26,
  },

  chunkFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 14,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    flexWrap: 'wrap',
    minHeight: 56,
  },

  chunkFooterTitle: {
    fontSize: 15,
    fontWeight: '700',
    flexShrink: 0,
  },

  chunkFooterSwatches: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 120,
  },

  chunkFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 2,
  },

  chunkFooterSwatch: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
  },

  leftColumn: {
    flex: 0.6,
    gap: 16,
    minHeight: 0,
  },

  imagePreviewCard: {
    padding: 16,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },

  imagePreview: {
    aspectRatio: 4 / 5,
    borderRadius: 12,
    marginBottom: 12,
    alignSelf: 'center',
  },

  rightColumn: {
    flex: 0.4,
    gap: 16,
    minHeight: 0,
  },

  cardContent: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },

  imageSection: {
    alignItems: 'center',
    flex: 0.5,
  },

  analysisSection: {
    flex: 0.5,
    gap: 16,
  },

  mainImage: {
    borderRadius: 12,
    marginBottom: 12,
  },

  imageActions: {
    alignItems: 'center',
  },

  analysisPreviewCard: {
    padding: 16,
    alignItems: 'flex-start',
    backgroundColor: 'transparent',
  },

  analysisTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },

  changeImageButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    minWidth: 100,
  },

  analysisCard: {
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
    width: '100%',
    flex: 1,
  },

  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },

  skinToneInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },

  colorSwatch: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    flexShrink: 0,
  },

  skinToneDetails: {
    flex: 1,
    minWidth: 0,
  },

  skinToneCategory: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
    flexWrap: 'wrap',
    maxWidth: '100%',
  },

  skinToneConfidence: {
    fontSize: 14,
    fontWeight: '500',
  },

  // Combined Analysis Styles
  skinToneSection: {
    marginBottom: 16,
  },

  // Undertone Analysis Styles
  undertoneSection: {
    marginBottom: 16,
  },

  undertoneInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },

  undertoneLabel: {
    fontSize: 14,
    fontWeight: '600',
  },

  undertoneChip: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
  },

  undertoneChipText: {
    fontSize: 12,
    fontWeight: '700',
    color: palette.white,
  },

  loadingText: {
    fontSize: 14,
    fontStyle: 'italic',
  },

  // Color Palette Styles
  paletteTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
  },

  paletteDescription: {
    fontSize: 16,
    marginBottom: 12,
    lineHeight: 24,
    flexWrap: 'wrap',
    flexShrink: 1,
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
    marginTop: 0,
  },

  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 10 : 8,
    justifyContent: 'flex-start',
    maxWidth: '100%',
    alignContent: 'flex-start',
  },

  colorGridMobile: {
    justifyContent: 'flex-start',
    gap: Platform.OS === 'web' ? 10 : 8,
    width: '100%',
  },

  colorSwatchItem: {
    width: Platform.OS === 'web' ? 48 : 36,
    height: Platform.OS === 'web' ? 48 : 36,
  },

  colorSwatchBox: {
    width: Platform.OS === 'web' ? 48 : 36,
    height: Platform.OS === 'web' ? 48 : 36,
    borderRadius: Platform.OS === 'web' ? 10 : 8,
    borderWidth: 1.5,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },

  colorSwatchLarge: {
    width: 40,
    height: 55,
    borderRadius: 10,
    borderWidth: 2,
    flexShrink: 0,
  },

  avoidColorSwatch: {
    opacity: 0.6,
  },

  // Makeup Recommendations Styles
  makeupCard: {
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
    width: '100%',
  },

  makeupTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 20,
  },

  makeupGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: Platform.OS === 'web' ? 10 : 8,
    overflow: 'visible',
    maxWidth: '100%',
  },

  makeupGridMobile: {
    justifyContent: 'flex-start',
    gap: Platform.OS === 'web' ? 10 : 8,
    paddingHorizontal: 0,
    width: '100%',
  },

  makeupItem: {
    alignItems: 'center',
    minWidth: Platform.OS === 'web' ? 56 : 44,
    flexShrink: 1,
  },

  makeupItemMobile: {
    width: '20%',
    maxWidth: '20%',
    flexBasis: '20%',
    minWidth: 0,
    flexShrink: 0,
    flexGrow: 0,
    alignItems: 'flex-start',
  },

  makeupLabel: {
    fontSize: 11,
    marginTop: 6,
    textAlign: 'center',
    fontWeight: '500',
  },

  makeupLabelMobile: {
    fontSize: 10,
    lineHeight: 12,
    textAlign: 'center',
    flexWrap: 'wrap',
  },

  makeupColorSwatch: {
    width: Platform.OS === 'web' ? 48 : 36,
    height: Platform.OS === 'web' ? 48 : 36,
    borderRadius: Platform.OS === 'web' ? 10 : 8,
    borderWidth: 1.5,
    minWidth: Platform.OS === 'web' ? 48 : 36,
  },

  // Error handling
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 8,
    marginTop: 16,
  },

  errorText: {
    fontSize: 12,
    flex: 1,
  },

  analysisFailureContainer: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    flexGrow: 0,
  },
  analysisFailureIconWrap: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  analysisFailureTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  analysisFailureMessage: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 16,
  },
  analysisFailureTips: {
    width: '100%',
    gap: 8,
    marginBottom: 16,
  },
  analysisFailureTipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  analysisFailureTipText: {
    fontSize: 14,
    flex: 1,
  },
  analysisFailurePreview: {
    width: 120,
    height: 120,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  analysisFailureActions: {
    width: '100%',
    gap: 12,
  },
  analysisFailurePrimaryBtn: {
    width: '100%',
  },
  analysisFailureSecondaryBtn: {
    width: '100%',
  },

  analyzeContainer: {
    marginTop: 24,
  },

  primaryButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },

  premiumUpsell: {
    marginTop: 24,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  premiumHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },

  premiumTitle: {
    fontSize: 16,
    fontWeight: '700',
  },

  premiumDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },

  premiumFeatures: {
    gap: 8,
    marginBottom: 20,
  },

  premiumFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  premiumFeatureText: {
    fontSize: 14,
  },

  premiumButton: {
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },

  realTimeHeader: {
    padding: 20,
    borderRadius: 12,
    marginBottom: 5,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },

  realTimeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },

  realTimeTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },

  realTimeSubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },

  cameraWrapper: {
    alignSelf: 'center',
    aspectRatio: 4 / 5,
    marginVertical: 20,
    borderRadius: 12,
    overflow: 'hidden',
  },

  progressContainer: {
    margin: 16,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
  },

  progressText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
  },

  progressBar: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },

  progressFill: {
    height: '100%',
    borderRadius: 4,
  },

  unknownSkinToneMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 193, 7, 0.3)',
  },

  unknownSkinToneText: {
    fontSize: 13,
    lineHeight: 18,
    flex: 1,
  },

  toastContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
    alignItems: 'center',
  },

  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    maxWidth: 400,
    width: '90%',
    height: 70,
  },

  toastText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },

  bottomActionsContainer: {
    flexDirection: 'column',
    gap: 12,
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },

  buttonContainer: {
    gap: 12,
    alignItems: 'center',
    width: '100%',
    maxWidth: 500,
  },

  buttonContainerMobile: {
    flexDirection: 'row',
  },

  buttonContainerDesktop: {
    flexDirection: 'row',
  },

  actionButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
    minWidth: 120,
    maxWidth: 200,
    flex: 1,
  },

  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
});

// Web-specific upload container with drag and drop
const WebUploadContainer = ({
  colors,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onPickImage,
  onTakePicture,
}: {
  colors: {
    primary: string;
    theme_color: string;
    text: string;
    secondary: string;
    card: string;
  };
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPickImage: () => void;
  onTakePicture: () => void;
}) => {
  if (Platform.OS !== 'web') return null;

  return (
    <div
      style={{
        borderWidth: 2,
        borderStyle: 'dashed',
        borderRadius: 16,
        padding: 30,
        marginBottom: 24,
        width: '75%',
        alignSelf: 'center',
        minHeight: 200,
        borderColor: isDragOver ? colors.primary : colors.theme_color,
        backgroundColor: isDragOver ? `${colors.primary}10` : 'transparent',
        transition: 'all 0.2s ease',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 24,
          cursor: 'pointer',
        }}
        onClick={onPickImage}
      >
        <div
          style={{
            width: 64,
            height: 64,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Ionicons name="images" size={32} color={colors.primary} />
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: 5,
            color: colors.text,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            lineHeight: '20px',
          }}
        >
          Upload an Image
        </div>
        <div
          style={{
            fontSize: 12,
            color: colors.secondary,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            lineHeight: '16px',
            textAlign: 'center',
          }}
        >
          Choose from Gallery or drag and drop an image here
        </div>
      </div>

      <Button
        title="Take Photo"
        onPress={onTakePicture}
        icon={<Ionicons name="camera" size={20} color={colors.card} />}
        style={styles.cameraButton}
        textStyle={styles.cameraText}
        variant="primary"
        trackClick={true}
        trackingLocation="color-analysis-screen"
        trackingData={{
          button_name: 'Take Photo',
          button_type: 'take_photo',
          screen: 'color_analysis',
        }}
      />
    </div>
  );
};

export default ColourAnalysisScreen;
